'use client'

import {
  AtSign, CalendarClock, Clock, Gift, GraduationCap, Heading, HeartHandshake, Hourglass, Image as ImageIcon, Landmark, Link as LinkIcon,
  Mail, MapPin, MessageCircleQuestionMark, MessagesSquare, Music, NotebookPen, PawPrint, Play, QrCode, SeparatorHorizontal, ShoppingBag, Ticket,
  TicketPercent, Type, Vote,
} from 'lucide-react'

import type { BlockContent, BlockGroup, BlockType, Entitlements, FeatureFlag } from '@/lib/dashboard'
import type { MessageKey } from '@/lib/i18n/translate'

/**
 * The client's half of the block catalog.
 *
 * Icons and grouping only. Default sizes, the plan flag each type needs, and
 * what content is allowed all come from `GET /blocks/catalog`, so the palette
 * can never offer something the server would reject.
 */
export const BLOCK_ICON: Record<BlockType, typeof LinkIcon> = {
  link: LinkIcon,
  social: AtSign,
  text: Type,
  image: ImageIcon,
  video: Play,
  audio: Music,
  product: ShoppingBag,
  booking: CalendarClock,
  ticket: Ticket,
  donate: HeartHandshake,
  email: Mail,
  map: MapPin,
  poll: Vote,
  countdown: Hourglass,
  wishlist: Gift,
  cv: GraduationCap,
  qa: MessageCircleQuestionMark,
  pet: PawPrint,
  heading: Heading,
  divider: SeparatorHorizontal,
  qr: QrCode,
  hours: Clock,
  bank: Landmark,
  chat: MessagesSquare,
  promo: TicketPercent,
  blog: NotebookPen,
}

/**
 * The catalog's "Түгээмэл" row, in order.
 *
 * A prediction, not a count: there is no usage data behind it yet. A link and
 * the socials are what a link-in-bio page is for, a lifestyle page is mostly
 * pictures, and the anonymous Q&A is the format people share most. Swap this
 * for real numbers from the backend once there are enough pages to count.
 */
export const POPULAR_TYPES: BlockType[] = ['link', 'social', 'image', 'qa']

/** Catalog section order, matching the design's chip row. */
export const BLOCK_GROUPS: BlockGroup[] = ['basic', 'media', 'interactive', 'commerce', 'support', 'layout']

/** Types that render a media placeholder rather than an icon tile. */
export const MEDIA_TYPES: BlockType[] = ['image', 'video', 'map']

export const isMediaType = (type: BlockType) => MEDIA_TYPES.includes(type)

/** Which content fields a type actually edits, in the order they appear. */
export type BlockField = 'title' | 'sub' | 'url' | 'brand' | 'cta' | 'amounts' | 'done'

export const BLOCK_FIELDS: Record<BlockType, BlockField[]> = {
  link: ['title', 'sub', 'url'],
  social: ['title', 'sub', 'brand', 'url'],
  text: ['title'],
  image: ['title', 'sub'],
  video: ['title', 'url'],
  audio: ['title', 'url'],
  // No generic fields. A product block's words come from the product it points
  // at, and the one thing it owns -- which photo fronts the card -- is picked
  // from the product's own gallery, so `BlockConfig` renders it directly.
  product: [],
  booking: ['title', 'sub', 'cta'],
  // No description. A ticket card's second line is the event's own date and
  // venue and its third is the price, all read from the record -- so a `sub`
  // typed here was stored and never drawn anywhere.
  ticket: ['title', 'cta'],
  donate: ['title', 'sub', 'cta', 'amounts'],
  email: ['title', 'sub', 'cta'],
  // Title only. A map block renders from `coords`, which `MapPicker` sets; the
  // URL field it used to ask for was stored and never read. The description
  // went too: the card shows a map, and a line of prose under it competed with
  // the thing people actually look at.
  map: ['title'],
  /*
   * Just the line of text each of these has. Everything else they hold is a
   * list or a date, which `BlockConfig` draws with an editor of its own: the
   * poll's options, the countdown's moment, the wishlist's gifts, the CV's
   * lines.
   */
  poll: ['title'],
  countdown: ['title', 'done', 'url'],
  wishlist: ['title', 'sub'],
  cv: ['title', 'sub', 'url'],
  qa: ['title', 'sub', 'cta'],
  // The name. Its look and its care have editors of their own in `BlockConfig`.
  pet: ['title'],
  /*
   * The words only, or nothing at all for a divider. Size, alignment, the QR's
   * mode and the week's hours are each type's own editor in `BlockConfig`.
   */
  heading: ['title', 'sub'],
  divider: [],
  qr: ['title'],
  hours: ['title', 'sub'],
  // The words only. Accounts, channels and the code have editors of their own in `BlockConfig`.
  bank: ['title'],
  chat: ['title', 'sub'],
  promo: ['title', 'sub'],
  // Nothing generic. Which post it shows, the mode and the list length are its
  // own editor in `BlockConfig`, and the words come from the post itself.
  blog: [],
}

/**
 * The one field edited as formatted text rather than as a line.
 *
 * A text block is a statement on somebody's page, so it is the only place worth
 * the cost of markup: everything else here is a label, a URL or a caption,
 * where bold and a font choice would be noise the card cannot render anyway.
 */
export const isRichField = (type: BlockType, field: BlockField) => type === 'text' && field === 'title'

/**
 * The placeholder for one field on one type.
 *
 * Per type, not per field, because the same field means different things
 * depending on what it is attached to: `sub` under a donate block is a reason
 * to give, under a link it is what the link leads to, and under an event it is
 * where the event is. A single shared "type here" told somebody the box
 * accepted text, which they could already see.
 *
 * Falls back to the generic prompt where a type has nothing specific worth
 * saying, so adding a block type does not require touching four locale files
 * before its form is usable.
 */
export const fieldPlaceholderKey = (type: BlockType, field: BlockField) =>
  `dashboard.placeholder.${type}.${field}` as MessageKey

export const typeNameKey = (type: BlockType) => `dashboard.blockType.${type}` as MessageKey
export const typeDescKey = (type: BlockType) => `dashboard.blockDesc.${type}` as MessageKey
export const groupNameKey = (group: BlockGroup) => `dashboard.catalog.${group}` as MessageKey
export const fieldLabelKey = (field: BlockField) =>
  `dashboard.config.field${field.charAt(0).toUpperCase()}${field.slice(1)}` as MessageKey
export const featureLabelKey = (flag: FeatureFlag) => `dashboard.feature.${flag}` as MessageKey

/**
 * Starting content for a freshly added block.
 *
 * Only the types the design gives a default for. The rest open empty, which is
 * the honest state for a link with no URL yet.
 */
const NEW_TITLE_KEYS: Partial<Record<BlockType, string>> = {
  link: 'link', social: 'social', text: 'text', image: 'image',
  video: 'video', audio: 'audio', donate: 'donate', email: 'email', map: 'map',
  poll: 'poll', countdown: 'countdown', wishlist: 'wishlist',
  heading: 'heading', qr: 'qr', hours: 'hours',
  bank: 'bank', chat: 'chat',
}

export function startingContent(type: BlockType, t: (key: MessageKey) => string): BlockContent {
  const key = NEW_TITLE_KEYS[type]
  const content: BlockContent = key ? { title: t(`dashboard.blockNew.${key}` as MessageKey) } : {}
  // A new video captions over its own frame, the way a picture does. Written
  // onto the block rather than made the fallback for a missing `caption`, so
  // every video added before this keeps the strip underneath it was saved with.
  if(type === 'video') content.caption = 'over'
  // A new hours block opens on an ordinary working week, so the card says something true-ish
  // the moment it lands rather than "no hours", and the owner edits from there.
  if(type === 'hours') content.week = [[], ...Array.from({ length: 5 }, () => [{ startMinute: 540, endMinute: 1080 }]), []]
  return content
}

/** Whether the current plan allows this type, and why not if it does not. */
export function isTypeLocked(requires: FeatureFlag | null, entitlements: Entitlements | null): boolean {
  if(!requires) return false
  if(!entitlements) return false
  return !entitlements[requires]
}

/**
 * Whether a type needs ДАН this account does not have yet. Separate from the
 * plan lock, because the way out is verifying, not paying.
 */
export function isDanLocked(requiresDan: boolean | undefined, verified: boolean): boolean {
  return !!requiresDan && !verified
}

/**
 * Avatar upload limits, kept in step with the backend middleware: MAX_MB there
 * is 10, and these are the image types its supportedTypes list accepts.
 */
/**
 * How many pictures one image block may hold.
 *
 * Well under the backend's own limit of 30, and equal to what the upload
 * endpoint accepts in a single request, so a full selection is one round trip.
 * The cap is here rather than in the schema so it can be raised without a
 * migration: rows written under a larger limit stay valid either way.
 */
export const MAX_IMAGES = 10

/*
 * The ceiling for any picture: an avatar, a block image, a product photo.
 *
 * Five rather than ten. These are all displayed images that get resized on the
 * way in, so the extra megabytes bought nothing on screen and cost every
 * visitor the bandwidth. A phone photograph is comfortably under it.
 *
 * The backend enforces the same number in `upload.middleware.js`. The two must
 * agree: a client limit above the server's rejects files after uploading them,
 * and one below it silently forbids what the server would have taken.
 */
export const AVATAR_MAX_MB = 5
export const AVATAR_TYPES = 'image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif'
